import { useState } from 'react';
import { Check, Sparkles, Car } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

const Services = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);

  const packages = [
    {
      name: 'Nettoyage Intégral',
      price: '',
      pricingDetails: [
        { type: 'Citadine', price: '230€', icon: 'https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/74vorods_image.png' },
        { type: 'Berline', price: '280€', icon: 'https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/zgmnhmuz_image.png' },
        { type: 'SUV/Sportif', price: '330€', icon: 'https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/2y6od6gt_image.png' }
      ],
      duration: '',
      description: '',
      features: [
        'Nettoyage intérieur approfondi',
        'Nettoyage extérieur détaillé',
        'Compartiment moteur',
        'Traitement cuir & textiles',
        'Décontamination',
        'Protection cire',
      ],
      image: 'https://customer-assets.emergentagent.com/job_dc9381ad-1d56-41f1-8e2a-40efea919a71/artifacts/ej5igr3t_image.png',
      popular: false,
    },
    {
      name: 'Correction du Vernis',
      price: '',
      duration: '',
      description: '',
      correctionSections: [
        {
          title: 'Lustrage',
          pricing: [
            { type: 'Citadine', price: '150€' },
            { type: 'Berline', price: '200€' },
            { type: 'SUV/Sportif', price: '250€' }
          ],
          correction: '50–60%',
          features: [
            'Suppression micro-rayures',
            'Amélioration profonde de la brillance',
            'Finition miroir'
          ]
        },
        {
          title: 'Polissage & Lustrage',
          pricing: [
            { type: 'Citadine', price: '280€' },
            { type: 'Berline', price: '330€' },
            { type: 'SUV/Sportif', price: '380€' }
          ],
          correction: '80–95%',
          features: [
            'Correction des défauts majeurs',
            'Restauration de la profondeur du vernis',
            'Finition haut de gamme'
          ]
        }
      ],
      features: [],
      image: 'https://customer-assets.emergentagent.com/job_7b719bdf-e077-4e3c-9fc3-d2b972d92c26/artifacts/wdcd2xzq_image.png',
      popular: false,
    },
    {
      name: 'Protection du Vernis',
      price: '',
      duration: '',
      description: '',
      protectionSections: [
        {
          title: 'Protection Nano',
          duration: '𝟭 an',
          pricing: [
            { type: 'Citadine', price: '80€' },
            { type: 'Berline', price: '130€' },
            { type: 'SUV/Sportif', price: '180€' }
          ]
        },
        {
          title: 'Protection Céramique',
          duration: '𝟱 ans',
          pricing: [
            { type: 'Citadine', price: '270€' },
            { type: 'Berline', price: '320€' },
            { type: 'SUV/Sportif', price: '370€' }
          ]
        }
      ],
      sharedFeatures: [
        'Application professionnelle',
        'Séchage contrôlé sous lampe thermique',
        'Garantie de qualité'
      ],
      features: [],
      image: 'https://customer-assets.emergentagent.com/job_7b719bdf-e077-4e3c-9fc3-d2b972d92c26/artifacts/xnoo5ewy_image.png',
      popular: false,
    },
  ];

  const additionalServices = [
    {
      title: 'Céramique jantes (avec démontage)',
      price: '100–200€',
      description: 'Protection céramique durable pour vos jantes',
    },
    {
      title: 'Céramique vitres',
      price: '100–200€',
      description: 'Effet déperlant extrême, meilleure visibilité',
    },
    {
      title: 'Polissage vitres léger',
      price: '100–200€',
      description: 'Élimination des micro-rayures sur les vitres',
    },
    {
      title: 'Moto detailing + Nano',
      price: '150€',
      description: 'Lustrage et protection nano pour moto',
    },
    {
      title: 'Moto detailing + Céramique',
      price: '250€',
      description: 'Lustrage et protection céramique pour moto',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d9fd72fd-f35d-49e6-8f9e-e0391db52cc1/artifacts/qff2sz0t_image.png)',
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center text-white px-4">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="text-[#c9a961]" size={24} />
            <span className="text-[#c9a961] font-medium tracking-wider uppercase">Nos Prestations</span>
          </div>
          <h1 className="hero-title text-5xl md:text-7xl mb-4">Nos Services</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Des forfaits adaptés à chaque besoin pour sublimer votre véhicule
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="section-title text-4xl md:text-5xl mb-6 text-[#1a1a1a] gold-line inline-block pb-4">
              Notre Philosophie
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nous nous concentrons sur les corrections approfondies de peinture et les polissages professionnels, où chaque détail compte.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Nous ne proposons pas beaucoup de traitements standard sans effet ; nous travaillons avec précision et méthode, afin que chaque voiture obtienne un éclat sublime sans hologrammes, une correction maximale de la peinture et une finition haut de gamme.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Nos Prestations
            </h2>
            <p className="text-gray-600 text-lg">Un système structuré en trois niveaux : Nettoyage / Correction / Protection</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl cursor-pointer ${
                  pkg.popular ? 'border-2 border-[#c9a961] shadow-xl scale-105' : 'hover:scale-105'
                }`}
                onClick={() => setSelectedPackage(pkg.name)}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-[#c9a961] text-white px-4 py-1 text-sm font-semibold">
                      Populaire
                    </Badge>
                  </div>
                )}

                <div className="relative h-80 md:h-72 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    style={{
                      objectPosition: pkg.name === 'Nettoyage Intégral' ? 'center 70%' : 
                                     pkg.name === 'Correction du Vernis' ? 'center 75%' : 
                                     pkg.name === 'Protection du Vernis' ? 'center 60%' : 'center',
                      objectFit: 'cover'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <h3 className="section-title text-3xl text-white mb-1">{pkg.name}</h3>
                    <p className="text-gray-300 text-sm">{pkg.description}</p>
                  </div>
                </div>

                <CardHeader>
                  {pkg.protectionSections ? (
                    // Affichage spécial pour PROTECTION DU VERNIS avec deux sections
                    <div className="space-y-6">
                      {pkg.protectionSections.map((section, sectionIdx) => (
                        <div key={sectionIdx} className={sectionIdx > 0 ? 'pt-4 border-t border-gray-200' : ''}>
                          <h4 className="text-lg font-bold text-[#1a1a1a] mb-3">
                            {section.title} <span className="text-[#c9a961] font-bold">({section.duration})</span>
                          </h4>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            {section.pricing.map((item, idx) => (
                              <div key={idx} className="flex flex-col items-center p-2 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-600 mb-1">{item.type}</p>
                                <p className="text-sm font-bold text-[#1a1a1a]">{item.price}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {pkg.sharedFeatures && pkg.sharedFeatures.length > 0 && (
                        <div className="pt-4 border-t border-gray-200">
                          <ul className="space-y-2">
                            {pkg.sharedFeatures.map((feature, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <Check className="text-[#c9a961] mt-0.5 flex-shrink-0" size={16} />
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : pkg.correctionSections ? (
                    // Affichage spécial pour CORRECTION DU VERNIS avec deux sections
                    <div className="space-y-6">
                      {pkg.correctionSections.map((section, sectionIdx) => (
                        <div key={sectionIdx} className={sectionIdx > 0 ? 'pt-4 border-t border-gray-200' : ''}>
                          <h4 className="text-lg font-bold text-[#1a1a1a] mb-3">{section.title}</h4>
                          <div className="grid grid-cols-3 gap-2 text-center mb-3">
                            {section.pricing.map((item, idx) => (
                              <div key={idx} className="flex flex-col items-center p-2 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-600 mb-1">{item.type}</p>
                                {section.title === 'Polissage & Lustrage' && (
                                  <p className="text-[10px] text-gray-500">à partir de</p>
                                )}
                                <p className="text-sm font-bold text-[#1a1a1a]">{item.price}</p>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-[#c9a961] font-medium mb-2">Correction estimée : {section.correction}</p>
                          <ul className="space-y-1">
                            {section.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <Check className="text-[#c9a961] mt-0.5 flex-shrink-0" size={16} />
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : pkg.pricingDetails ? (
                    // Affichage spécial pour NETTOYAGE INTÉGRAL
                    <div className="space-y-3">
                      {pkg.price && <CardTitle className="text-2xl font-bold text-[#1a1a1a] mb-3">{pkg.price}</CardTitle>}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        {pkg.pricingDetails.map((detail, idx) => (
                          <div key={idx} className="flex flex-col items-center p-2 bg-gray-50 rounded-lg">
                            <span className="text-3xl mb-1">{detail.emoji}</span>
                            <p className="text-xs text-gray-600 mb-1">{detail.type}</p>
                            <p className="text-[10px] text-gray-500">à partir de</p>
                            <p className="text-sm font-bold text-[#1a1a1a]">{detail.price}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    // Affichage normal pour Platinum
                    <CardTitle className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-[#1a1a1a]">{pkg.price}</span>
                      <span className="text-sm text-gray-500 font-normal">Selon taille</span>
                    </CardTitle>
                  )}
                </CardHeader>

                <CardContent>
                  {pkg.features && pkg.features.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <Check className="text-[#c9a961] mt-0.5 flex-shrink-0" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link
                    to="/reservation"
                    className="w-full block text-center px-6 py-3 bg-[#1a1a1a] text-white rounded-full font-semibold hover:bg-[#2a2a2a] transition-all duration-300"
                    data-testid={`book-${pkg.name.toLowerCase().replace(/ /g, '-')}`}
                  >
                    Réserver ce forfait
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Options Supplémentaires
            </h2>
            <p className="text-gray-600 text-lg">Personnalisez votre prestation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`additional-service-${index}`}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  <div className="flex items-baseline">
                    <span className="text-sm font-bold text-[#c9a961]">{service.price}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_d9fd72fd-f35d-49e6-8f9e-e0391db52cc1/artifacts/o83ziipm_image.png"
                alt="SUKI detailing process"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="section-title text-4xl md:text-5xl text-[#1a1a1a] gold-line pb-4">
                Ce que nous faisons
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Nous nous concentrons sur l'extérieur et la protection :
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <Check className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Lavage détaillé de l'extérieur et décontamination</h3>
                    <p className="text-gray-600">Nettoyage en profondeur pour éliminer toutes les impuretés</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Polissage professionnel et correction de peinture</h3>
                    <p className="text-gray-600">Correction des imperfections sans hologrammes</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-[#c9a961] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Protections haut de gamme</h3>
                    <p className="text-gray-600">Cires, revêtements nano et céramiques pour une protection durable</p>
                  </div>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed pt-4">
                Chez nous, chaque client profite bien plus qu'un simple "lustrage" : nous proposons de véritables traitements qui révèlent la beauté du véhicule et renforcent la longévité de sa peinture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="hero-title text-4xl md:text-5xl mb-6">Une question sur nos services ?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Notre équipe est à votre disposition pour vous conseiller
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/reservation"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#c9a961] text-white rounded-full text-lg font-semibold hover:bg-[#b8984e] transition-all duration-300"
              data-testid="services-cta-book"
            >
              Réserver maintenant
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-md text-white rounded-full text-lg font-semibold hover:bg-white/20 transition-all duration-300 border border-white/30"
              data-testid="services-cta-contact"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
